import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useApolloClient } from '@apollo/client'
import { GET_PROJECT_FLOW_QUERY, GET_FLOW_VERSION_QUERY, SET_FLOW_LINKS_MUTATION, SET_FLOW_MEMBERSHIP_MUTATION, UNLINK_TASKS_MUTATION, ARRANGING_FLOW_MUTATION, GENERATE_MUTATION } from '../query/gqlQueries'
import { useFrostTheme } from '../theme/frost'
import s from './FlowView.module.scss'
import FlowAddTasks from './FlowAddTasks'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import CloseIcon from '@mui/icons-material/Close'

// A project's flow, as the Work app shows it on the iPad and the Apple TV
// (Shared/Flow/FlowLayout.swift, FlowCanvas.swift, WorkTV/TVFlow.swift): the
// whole of its work from start to finish as a timeline — across is work
// time, each task as long as the hours it still needs, starting when
// everything before it is done; what can happen side by side stacks above and
// below. Arrows go from each task to what comes after it. It follows changes
// as they're made: arrange it on the iPad and the cards move here within a
// second or two. The layout is worked out exactly as the apps do it, so the
// website, the iPad and the TV agree. And it can be arranged here as on the
// iPad: the open tasks not in the flow wait in the tray; drag one onto a card
// to come after it, onto empty timeline to start with it, or a card back onto
// the tray to take it out (what was either side of it joins up).

const STANDARD_DAY_HOURS = 7       // WorkDay.standardHours
const DONE_HOURS = 0.5             // a done task keeps a sliver of the timeline
const PRIORITY = { urgent: 4, high: 3, medium: 2, med: 2, low: 1 }
const prio = (t) => PRIORITY[String(t.properties?.priority || '').toLowerCase()] || 1
const statusOf = (t) => {
  const v = String(t.status || '').toUpperCase()
  return ['BACKLOG', 'INPROGRESS', 'VALIDATING', 'DONE'].includes(v) ? v : 'BACKLOG'
}
const hoursOf = (t) => {
  if (statusOf(t) === 'DONE') return DONE_HOURS
  const budget = Number(t.properties?.timeBudget) || 0
  const spent = Number(t.properties?.timeSpent) || 0
  return Math.max(0.5, budget > 0 ? budget - spent : 1)
}
const fmtHours = (h) => (Math.round(h * 10) / 10).toString()

/** FlowLayout, in JavaScript: nodes with start, hours, slot (half rows) and state. */
export function layoutFlow(tasks, allLinks) {
  const byId = new Map(tasks.map(t => [t.identity, t]))
  const links = allLinks.filter(l => byId.has(l.from) && byId.has(l.to))
  const before = new Map(), after = new Map()
  for (const l of links) {
    if (!before.has(l.to)) before.set(l.to, []); before.get(l.to).push(l.from)
    if (!after.has(l.from)) after.set(l.from, []); after.get(l.from).push(l.to)
  }
  // In order: each task after everything it waits on; ties by status (done
  // first), then priority, then title — so it's stable.
  const rank = (t) => {
    const st = statusOf(t)
    return [st === 'DONE' ? 0 : (st === 'INPROGRESS' || st === 'VALIDATING') ? 1 : 2, -prio(t), t.properties?.title || '']
  }
  const cmp = (a, b) => { const x = rank(a), y = rank(b); return x[0] - y[0] || x[1] - y[1] || (x[2] < y[2] ? -1 : x[2] > y[2] ? 1 : 0) }
  const waiting = new Map(tasks.map(t => [t.identity, (before.get(t.identity) || []).length]))
  let ready = tasks.filter(t => waiting.get(t.identity) === 0).sort(cmp)
  const order = []
  while (ready.length) {
    const t = ready.shift()
    order.push(t)
    for (const next of after.get(t.identity) || []) {
      waiting.set(next, (waiting.get(next) || 0) - 1)
      if (waiting.get(next) === 0 && byId.has(next)) { ready.push(byId.get(next)); ready.sort(cmp) }
    }
  }
  // A loop shouldn't exist (the server refuses them); if one does, its tasks go at the end.
  const placedIds = new Set(order.map(t => t.identity))
  order.push(...tasks.filter(t => !placedIds.has(t.identity)).sort(cmp))

  const ends = new Map(), slotOf = new Map(), slotEnds = new Map()
  let rootCursor = 0
  let nodes = []
  const position = new Map(order.map((t, i) => [t.identity, i]))
  const kids = new Map([...after.entries()].map(([k, v]) => [k, [...v].sort((a, b) => (position.get(a) || 0) - (position.get(b) || 0))]))
  const free = (sl, start) => (slotEnds.get(sl) ?? -1) <= start + 0.001 && (slotEnds.get(sl + 1) ?? -1) <= start + 0.001
  for (const t of order) {
    const id = t.identity
    const start = Math.max(0, ...(before.get(id) || []).map(p => ends.get(p)).filter(v => v != null))
    const hours = hoursOf(t)
    const parents = (before.get(id) || []).filter(p => slotOf.has(p))
    // Where it would like to be: a starting task below the last one; a
    // follower fanned around what it follows — the 1st of 3 above, the 2nd level, the 3rd below.
    let desired
    if (parents.length) {
      const main = parents.reduce((m, p) => ((ends.get(p) || 0) > (ends.get(m) || 0) ? p : m), parents[0])
      const siblings = kids.get(main) || [id]
      const k = siblings.length
      const i = Math.max(0, siblings.indexOf(id))
      const base = parents.reduce((sum, p) => sum + slotOf.get(p), 0) / parents.length
      desired = Math.round(base + (2 * i - (k - 1)))
    } else {
      desired = rootCursor
    }
    let slot = desired
    search: for (let d = 0; d <= 400; d++) {
      for (const c of d === 0 ? [desired] : [desired + d, desired - d]) {
        if (free(c, start)) { slot = c; break search }
      }
    }
    slotEnds.set(slot, start + hours); slotEnds.set(slot + 1, start + hours)
    ends.set(id, start + hours); slotOf.set(id, slot)
    if (!parents.length) rootCursor = Math.max(rootCursor, slot + 2)
    const open = (before.get(id) || []).map(p => byId.get(p)).filter(p => p && statusOf(p) !== 'DONE')
    const st = statusOf(t)
    const state = st === 'DONE' ? 'done' : (st === 'INPROGRESS' || st === 'VALIDATING') ? 'doing' : open.length ? 'waiting' : 'ready'
    nodes.push({ id, task: t, start, hours, slot, state, waitingOn: open.map(p => p.properties?.title || '') })
  }
  const top = nodes.length ? Math.min(...nodes.map(n => n.slot)) : 0
  nodes = nodes.map(n => ({ ...n, slot: n.slot - top }))
  const slots = nodes.length ? Math.max(...nodes.map(n => n.slot)) + 2 : 0
  const left = nodes.filter(n => n.state !== 'done')
  const workLeft = left.reduce((sum, n) => sum + n.hours, 0)
  const span = left.length ? Math.max(...left.map(n => n.start + n.hours)) : 0
  return { nodes, links, slots, workLeft, span }
}

/** "34 h of work · about 5 days alone, 3 side by side". */
export function flowSummary(layout) {
  if (!(layout.workLeft > 0)) return layout.nodes.length ? 'All done' : 'Nothing in the flow yet'
  const days = (h) => { const d = h / STANDARD_DAY_HOURS; return d < 1 ? 'under a day' : `about ${Math.ceil(d)} day${d > 1 ? 's' : ''}` }
  const alone = days(layout.workLeft), together = days(layout.span)
  return `${fmtHours(layout.workLeft)} h of work · ${alone}` + (together !== alone ? ` alone, ${together} side by side` : '')
}

// The canvas's measures (the TV's, a little smaller for a desk).
const HOUR = 120, CARD_H = 124, ROW_GAP = 38, GAP = 72, MIN_W = 250, TOP = 40, LEFT = 24
const ROW = CARD_H + ROW_GAP

/** Where each card goes: at its start time, or just after what it follows if a short card is wider than its hours. */
function framesOf(layout) {
  const out = new Map(), rowRight = new Map(), before = new Map()
  for (const l of layout.links) { if (!before.has(l.to)) before.set(l.to, []); before.get(l.to).push(l.from) }
  for (const n of layout.nodes) {
    let x = LEFT + n.start * HOUR
    for (const p of before.get(n.id) || []) { const f = out.get(p); if (f) x = Math.max(x, f.x + f.w + GAP) }
    for (const sl of [n.slot, n.slot + 1]) { const r = rowRight.get(sl); if (r != null) x = Math.max(x, r + GAP) }
    const w = Math.max(MIN_W, n.hours * HOUR - GAP)
    const y = TOP + n.slot * ROW / 2
    out.set(n.id, { x, y, w, h: CARD_H })
    rowRight.set(n.slot, x + w); rowRight.set(n.slot + 1, x + w)
  }
  return out
}

/** Arrows fanned along the edges, in the order of the cards they join — as FlowCanvas does. */
function arrowsOf(links, frames) {
  const out = new Map(), into = new Map()
  for (const l of links) {
    if (!out.has(l.from)) out.set(l.from, []); out.get(l.from).push(l)
    if (!into.has(l.to)) into.set(l.to, []); into.get(l.to).push(l)
  }
  const spread = (list, r, other) => {
    const sorted = [...list].sort((a, b) => other(a) - other(b))
    const k = sorted.length
    return new Map(sorted.map((l, i) => [l, k === 1 ? r.y + r.h / 2 : r.y + r.h * (0.2 + 0.6 * i / (k - 1))]))
  }
  const fromY = new Map(), toY = new Map()
  for (const [id, list] of out) { const r = frames.get(id); if (r) for (const [l, y] of spread(list, r, (x) => { const f = frames.get(x.to); return f ? f.y + f.h / 2 : 0 })) fromY.set(l, y) }
  for (const [id, list] of into) { const r = frames.get(id); if (r) for (const [l, y] of spread(list, r, (x) => { const f = frames.get(x.from); return f ? f.y + f.h / 2 : 0 })) toY.set(l, y) }
  return links.map(l => {
    const a = frames.get(l.from), b = frames.get(l.to)
    if (!a || !b) return null
    const from = { x: a.x + a.w + 4, y: fromY.get(l) ?? a.y + a.h / 2 }
    const to = { x: b.x - 6, y: toY.get(l) ?? b.y + b.h / 2 }
    const head = 15, lead = 10
    const p1 = { x: from.x + lead, y: from.y }, p2 = { x: to.x - head - lead, y: to.y }
    const dx = Math.max(14, (p2.x - p1.x) / 2)
    const curve = Math.abs(p2.y - p1.y) < 0.5 ? `L ${p2.x} ${p2.y}` : `C ${p1.x + dx} ${p1.y}, ${p2.x - dx} ${p2.y}, ${p2.x} ${p2.y}`
    const d = `M ${from.x} ${from.y} L ${p1.x} ${p1.y} ${curve} L ${to.x - head + 1} ${to.y}`
    const hw = 13
    const headPath = `M ${to.x} ${to.y} L ${to.x - head} ${to.y - hw / 2} Q ${to.x - head + 4} ${to.y} ${to.x - head} ${to.y + hw / 2} Z`
    return { key: `${l.from}>${l.to}`, d, headPath }
  }).filter(Boolean)
}

const STATE = {
  done: { label: 'Done', icon: '✓' },
  doing: { label: 'Doing', icon: '●' },
  ready: { label: 'Ready', icon: '▶' },
  waiting: { label: 'Waiting', icon: '◷' },
}

export default function FlowView({ project, me, avatars = {}, onOpenTask, onClose }) {
  const client = useApolloClient()
  const [theme, toggleTheme] = useFrostTheme()
  const [flow, setFlow] = useState(null)
  const [error, setError] = useState('')
  const versionRef = useRef(null)

  const load = async () => {
    try {
      const { data } = await client.query({ query: GET_PROJECT_FLOW_QUERY, variables: { project }, fetchPolicy: 'network-only' })
      setFlow(data.getProjectFlow)
      versionRef.current = data.getProjectFlow?.version
      setError('')
    } catch (e) { setError(e.message) }
  }

  useEffect(() => {
    let alive = true
    load()
    // Follows the iPad: a cheap check every second and a half.
    const id = setInterval(async () => {
      if (!alive || document.hidden) return
      try {
        const { data } = await client.query({ query: GET_FLOW_VERSION_QUERY, variables: { project }, fetchPolicy: 'network-only' })
        if (alive && data?.getFlowVersion && data.getFlowVersion !== versionRef.current) load()
      } catch { /* the next check will tell */ }
    }, 1500)
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => { alive = false; clearInterval(id); window.removeEventListener('keydown', onKey) }
  // eslint-disable-next-line
  }, [project])

  // The project's tasks, as on the iPad — never subtasks, nor someone else's personal tasks.
  const tasks = useMemo(() => (flow?.tasks || []).filter(t => {
    const p = t.properties || {}
    const hidden = p.personal && String(p.username || '').toLowerCase() !== String(me || '').toLowerCase()
    return !hidden && !p.parentId
  }), [flow, me])
  const links = useMemo(() => flow?.links || [], [flow])
  const linked = useMemo(() => new Set(links.flatMap(l => [l.from, l.to])), [links])
  const isPlaced = (t) => t.properties?.inFlow === true || linked.has(t.identity)
  // On the timeline: what's been placed, or is linked.
  const placed = useMemo(() => tasks.filter(isPlaced), [tasks, linked]) // eslint-disable-line
  // In the tray: the open tasks not placed yet — work in progress first, then by priority.
  const unplaced = useMemo(() => tasks.filter(t => !isPlaced(t) && statusOf(t) !== 'DONE').sort((a, b) => {
    const da = ['INPROGRESS', 'VALIDATING'].includes(statusOf(a)), db = ['INPROGRESS', 'VALIDATING'].includes(statusOf(b))
    if (da !== db) return da ? -1 : 1
    return prio(b) - prio(a) || String(a.properties?.title).localeCompare(String(b.properties?.title))
  }), [tasks, linked]) // eslint-disable-line
  // The Mac Studio's suggested order, shown dashed until it's used.
  const [proposal, setProposal] = useState(null)   // { links, why }
  const [suggesting, setSuggesting] = useState(false)
  const layout = useMemo(() => {
    if (!proposal) return layoutFlow(placed, links)
    const ids = new Set([...placed.map(t => t.identity), ...proposal.links.flatMap(l => [l.from, l.to])])
    return layoutFlow(tasks.filter(t => ids.has(t.identity)), proposal.links)
  }, [placed, links, proposal, tasks])
  const frames = useMemo(() => framesOf(layout), [layout])
  const arrows = useMemo(() => arrowsOf(layout.links, frames), [layout, frames])
  const width = Math.max(...[...frames.values()].map(f => f.x + f.w), LEFT) + 160
  const height = Math.max(TOP + layout.slots / 2 * ROW + 20, 360)
  const dayCount = Math.max(1, Math.ceil((width - LEFT) / HOUR / STANDARD_DAY_HOURS))

  // ── arranging, as on the iPad ──
  const [drag, setDrag] = useState(null)       // the task being dragged
  const [target, setTarget] = useState(null)   // { card: id } | 'start' | 'aside'
  const [menu, setMenu] = useState(null)       // the card whose menu is open
  const [busy, setBusy] = useState(false)
  const [adding, setAdding] = useState(false)   // the Add tasks sheet

  // "I'm arranging this" — so a TV (or another screen) can follow along.
  useEffect(() => {
    const ping = () => { if (!document.hidden) client.mutate({ mutation: ARRANGING_FLOW_MUTATION, variables: { project } }).catch(() => {}) }
    ping()
    const id = setInterval(ping, 20000)
    return () => clearInterval(id)
  // eslint-disable-next-line
  }, [project])

  const change = async (run) => {
    setBusy(true)
    try {
      const fresh = await run()
      if (fresh) { setFlow(fresh); versionRef.current = fresh.version }
      setError('')
    } catch (e) { setError(e.message); load() }
    setBusy(false)
  }
  const call = async (mutation, variables, key) => (await client.mutate({ mutation, variables })).data[key]

  /** Moves a task as an item in a list moves: out of where it was (what came
   *  before it now leads to what came after), then after a card, at the start, or aside. */
  const move = (id, place) => {
    if (place && place.card === id) return
    const before = links.filter(l => l.to === id).map(l => l.from)
    const following = links.filter(l => l.from === id).map(l => l.to)
    let next = links.filter(l => l.from !== id && l.to !== id).map(l => ({ from: l.from, to: l.to }))
    for (const b of before) for (const f of following) if (!next.some(l => l.from === b && l.to === f)) next.push({ from: b, to: f })
    if (place && place.card) next.push({ from: place.card, to: id })
    const task = tasks.find(t => t.identity === id)
    const wasOut = task ? !isPlaced(task) : false
    // Shown at once; the server catches up.
    setFlow(f => f && ({
      ...f,
      tasks: f.tasks.map(t => (t.identity === id ? { ...t, properties: { ...t.properties, inFlow: place !== 'aside' } } : t)),
      links: place === 'aside' ? next.filter(l => l.from !== id && l.to !== id) : next,
    }))
    change(async () => {
      if (place === 'aside') {
        await call(SET_FLOW_LINKS_MUTATION, { project, links: next }, 'setFlowLinks')
        return call(SET_FLOW_MEMBERSHIP_MUTATION, { project, taskIds: [id], inFlow: false }, 'setFlowMembership')
      }
      if (wasOut) await call(SET_FLOW_MEMBERSHIP_MUTATION, { project, taskIds: [id], inFlow: true }, 'setFlowMembership')
      return call(SET_FLOW_LINKS_MUTATION, { project, links: next }, 'setFlowLinks')
    })
  }
  /** Asks the Mac Studio for the order the open work should happen in — only
   *  real dependencies, so what can run side by side stays side by side (the
   *  same question the iPad asks). */
  const suggest = async () => {
    setSuggesting(true); setError('')
    try {
      const open = tasks.filter(t => statusOf(t) !== 'DONE')
      const ids = new Set(tasks.map(t => t.identity))
      const STATUS_NAME = { BACKLOG: 'Backlog', INPROGRESS: 'Doing', VALIDATING: 'Validating', DONE: 'Done' }
      const lines = open.map(t => {
        const p = t.properties || {}
        return `${t.identity} | ${p.title} | ${STATUS_NAME[statusOf(t)]} | ${fmtHours(hoursOf(t))} h | ${p.assignee || '-'} | ${String(p.description || '').slice(0, 140).replace(/\n/g, ' ')}`
      })
      const existing = links.map(l => `${l.from} -> ${l.to}`)
      const prompt = `You plan the order of work on a project for a small creative studio (design, 3D, video, photography, code).

Project: ${project}
Tasks (id | title | status | hours left | who | description):
${lines.join('\n')}

Already decided (before -> after): ${existing.length ? existing.join(', ') : 'nothing yet'}

Work out which tasks have to be finished before which can start. Link only real dependencies — a shoot before its edit, a design before it's built, a draft before its review. Tasks that could happen at the same time must NOT be linked to each other. Keep the decided links unless they're clearly wrong. No loops.

Return ONLY JSON: {"links":[{"from":"<id done first>","to":"<id that waits for it>"}],"why":"one or two sentences on the order"}`
      const { data } = await client.mutate({ mutation: GENERATE_MUTATION, variables: { prompt, maxTokens: 1500, json: true } })
      const text = String(data?.generate?.text || '')
      const a = text.indexOf('{'), b = text.lastIndexOf('}')
      let root = null
      try { root = JSON.parse(text.slice(a, b + 1)) } catch { root = null }
      if (!root || !Array.isArray(root.links)) throw new Error("The Mac Studio's answer wasn't readable. Try again.")
      const found = []
      for (const r of root.links) {
        const f = String(r?.from ?? ''), t = String(r?.to ?? '')
        if (f && t && f !== t && ids.has(f) && ids.has(t) && !found.some(l => l.from === f && l.to === t)) found.push({ from: f, to: t })
      }
      if (!found.length) throw new Error("The Mac Studio didn't find an order for these.")
      setProposal({ links: found, why: typeof root.why === 'string' ? root.why : '' })
    } catch (e) {
      setError(/530|Failed to fetch|Load failed/.test(e.message || '') ? 'The Mac Studio isn’t answering — it may be asleep.' : e.message)
    }
    setSuggesting(false)
  }

  const useProposal = () => {
    if (!proposal) return
    const ids = [...new Set(proposal.links.flatMap(l => [l.from, l.to]))]
    const chosen = proposal.links
    change(async () => {
      await call(SET_FLOW_MEMBERSHIP_MUTATION, { project, taskIds: ids, inFlow: true }, 'setFlowMembership')
      return call(SET_FLOW_LINKS_MUTATION, { project, links: chosen }, 'setFlowLinks')
    }).then(() => setProposal(null))
  }

  const unlink = (before, after) => change(() => call(UNLINK_TASKS_MUTATION, { project, before, after }, 'unlinkTasks'))

  const startDrag = (e, id) => { setDrag(id); setMenu(null); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', id) }
  const endDrag = () => { setDrag(null); setTarget(null) }
  const over = (e, place) => { if (!drag) return; e.preventDefault(); e.stopPropagation(); setTarget(place) }
  const drop = (e, place) => { e.preventDefault(); e.stopPropagation(); const id = drag || e.dataTransfer.getData('text/plain'); endDrag(); if (id) move(id, place) }
  const isTarget = (place) => target && (target === place || (target.card && place && place.card === target.card))
  const titleOf = (id) => tasks.find(t => t.identity === id)?.properties?.title || 'a task'

  return (
    <div className={s.view} onClick={() => setMenu(null)}>
      <header className={s.head}>
        <div className={s.badge} aria-hidden>⟿</div>
        <div className={s.titles}>
          <h1>{project}</h1>
          <div className={s.summary}>{flow ? flowSummary(layout) : 'Loading the flow…'}</div>
        </div>
        {flow?.editor && String(flow.editor).toLowerCase() !== String(me || '').toLowerCase() && <div className={s.editor}><i />{flow.editor} is arranging this</div>}
        {busy && <div className={s.saving}>Saving…</div>}
        {error && <div className={s.error}>{error}</div>}
        <div className={s.buttons}>
          <button className={s.suggest} onClick={suggest} disabled={suggesting || !tasks.length || !!proposal}>
            {suggesting ? 'Thinking…' : '✦ Suggest an order'}
          </button>
          <button className={s.round} onClick={toggleTheme} aria-label="Toggle light or dark mode">{theme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}</button>
          <button className={s.round} onClick={onClose} aria-label="Close"><CloseIcon /></button>
        </div>
      </header>

      {proposal && (
        <div className={s.proposal}>
          <div className={s.proposalText}>
            <b>✦ Suggested order — dashed until you use it</b>
            {proposal.why && <span>{proposal.why}</span>}
          </div>
          <button className={s.discard} onClick={() => setProposal(null)}>Discard</button>
          <button className={s.useIt} onClick={useProposal} disabled={busy}>Use this order</button>
        </div>
      )}

      <div className={s.body}>
        <div className={`${s.scroller} ${isTarget('start') ? s.dropStart : ''}`}
          onDragOver={(e) => over(e, 'start')} onDragLeave={() => setTarget(null)} onDrop={(e) => drop(e, 'start')}>
          {flow && placed.length === 0 ? (
            <div className={s.empty}>
              <div className={s.emptyIcon}>⟿</div>
              <b>Nothing in this project's flow yet</b>
              <span>Drag a task here from the tray — then drag others onto it to come after it.</span>
            </div>
          ) : (
            <div className={s.canvas} style={{ width, height }}>
              {[...Array(dayCount)].map((_, d) => {
                const x = LEFT + d * STANDARD_DAY_HOURS * HOUR
                return (
                  <React.Fragment key={d}>
                    {d > 0 && <div className={s.dayLine} style={{ left: x, height }} />}
                    <div className={s.dayLabel} style={{ left: x + 8 }}>Day {d + 1}</div>
                  </React.Fragment>
                )
              })}
              <svg className={s.arrows} width={width} height={height}>
                {arrows.map(a => (
                  <g key={a.key} className={proposal ? s.ghost : ''}><path d={a.d} className={s.line} /><path d={a.headPath} className={s.head2} /></g>
                ))}
              </svg>
              {layout.nodes.map(n => {
                const f = frames.get(n.id)
                const p = n.task.properties || {}
                const avatar = avatars[p.assignee]
                const label = n.state === 'waiting' && n.waitingOn.length ? `After ${n.waitingOn[0]}` : STATE[n.state].label
                const befores = links.filter(l => l.to === n.id).map(l => l.from)
                return (
                  <div key={n.id} draggable onDragStart={(e) => startDrag(e, n.id)} onDragEnd={endDrag}
                    onDragOver={(e) => over(e, { card: n.id })} onDrop={(e) => drop(e, { card: n.id })}
                    className={`${s.card} ${s[n.state]} ${drag === n.id ? s.dragging : ''} ${isTarget({ card: n.id }) && drag !== n.id ? s.targeted : ''}`}
                    style={{ left: f.x, top: f.y, width: f.w, height: f.h }}
                    onClick={(e) => { e.stopPropagation(); onOpenTask && onOpenTask(n.task) }} role="button" tabIndex={0}>
                    <span className={s.stripe} />
                    <span className={s.cardBody}>
                      <span className={s.cardTitle}>{p.title}</span>
                      <span className={s.cardFoot}>
                        {avatar ? <img src={avatar} alt={p.assignee} className={s.avatar} /> : p.assignee ? <span className={s.avatarText}>{p.assignee[0]}</span> : null}
                        <span className={s.state}>{STATE[n.state].icon} {label}</span>
                        <span className={s.hours}>{fmtHours(n.hours)} h</span>
                      </span>
                    </span>
                    <button className={s.more} aria-label="More" onClick={(e) => { e.stopPropagation(); setMenu(m => (m === n.id ? null : n.id)) }}>⋯</button>
                    {menu === n.id && (
                      <div className={s.menu} onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => { setMenu(null); onOpenTask && onOpenTask(n.task) }}>Open the task</button>
                        {befores.map(b => <button key={b} onClick={() => { setMenu(null); unlink(b, n.id) }}>Not after “{titleOf(b)}”</button>)}
                        <button className={s.menuDanger} onClick={() => { setMenu(null); move(n.id, 'aside') }}>Take out of the flow</button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* The tray: the project's open tasks not in the flow yet. Drag one in; drag a card back here to take it out. */}
        <aside className={`${s.tray} ${isTarget('aside') ? s.trayTarget : ''}`}
          onDragOver={(e) => { if (drag && placed.some(t => t.identity === drag)) over(e, 'aside') }} onDragLeave={() => setTarget(null)} onDrop={(e) => drop(e, 'aside')}>
          <div className={s.trayHead}>Not in the flow <span>{unplaced.length}</span>
            <button className={s.trayNew} onClick={() => setAdding(true)} title="Say or type new tasks">＋ Add</button></div>
          <div className={s.trayNote}>Drag a task onto the timeline — onto a card to come after it, or onto empty space to start with it.</div>
          {unplaced.length === 0 && <div className={s.trayNote}>Every open task is in the flow.</div>}
          {unplaced.map(t => {
            const p = t.properties || {}
            return (
              <div key={t.identity} className={`${s.trayItem} ${drag === t.identity ? s.dragging : ''}`} draggable
                onDragStart={(e) => startDrag(e, t.identity)} onDragEnd={endDrag}>
                <span className={`${s.dot} ${s['p_' + String(p.priority || 'low').toLowerCase()]}`} />
                <span className={s.trayTitle}>{p.title}</span>
                <button className={s.trayAdd} title="Put it at the start" onClick={() => move(t.identity, 'start')}>＋</button>
              </div>
            )
          })}
        </aside>
      </div>
      {adding && <FlowAddTasks project={project} me={me} existing={tasks} onClose={() => setAdding(false)} onMade={() => load()} />}
    </div>
  )
}
